import type { CatalogProduct, CategoryRouteData, Route } from '../../types'
import { normalizePathname } from '../../routing'
import {
  buildCategoryBreadcrumbs,
  buildCategoryCanonicalPath,
  buildProductBreadcrumbs,
  inferProductBrand,
  pickCategoryImage,
  pickProductImage,
  trimText,
} from '../../seoHelpers'
import { DEFAULT_DESCRIPTION } from '../constants'
import { buildDefaultSeoAlternates } from '../localePolicy'
import type { ShopSeoData } from '../types'

export const buildProductSeo = ({
  route,
  productData,
  currentPathname,
  siteName,
}: {
  route: Route
  productData: CatalogProduct
  currentPathname?: string
  siteName: string
}): ShopSeoData => {
  const canonicalPath =
    normalizePathname(currentPathname || `/products/${encodeURIComponent(productData.slug)}`)
  const title = trimText(`${productData.name} | ${siteName}`, 70) || `${siteName}`
  const description =
    trimText(
      productData.description ||
        `Scopri ${productData.name} con disponibilita aggiornata e spedizione rapida.`,
      160,
    ) || DEFAULT_DESCRIPTION
  const image = pickProductImage(productData)
  const price = typeof productData.price === 'number' && productData.price ? productData.price : undefined
  const brand = inferProductBrand(productData)
  const breadcrumbs = buildProductBreadcrumbs(canonicalPath, productData.name)

  return {
    title,
    description,
    canonicalPath,
    alternates: buildDefaultSeoAlternates(canonicalPath),
    ...(image ? { image } : {}),
    noindex:
      Boolean(route.mode === 'product' && route.previewId) ||
      productData.status === 'draft' ||
      productData.status === 'archived',
    ogType: 'product',
    pageType: 'product',
    entityName: productData.name,
    breadcrumbs,
    product: {
      currency: 'EUR',
      ...(price !== undefined ? { price } : {}),
      availability:
        typeof (productData.purchasability?.availableStock ?? productData.availableStock ?? productData.quantity) === 'number'
        && Number(productData.purchasability?.availableStock ?? productData.availableStock ?? productData.quantity) > 0
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
      ...(productData._id ? { sku: productData._id } : {}),
      ...(brand ? { brand } : {}),
    },
  }
}

export const buildCategorySeo = ({
  categoryRouteData,
  siteName,
}: {
  categoryRouteData: CategoryRouteData
  siteName: string
}): ShopSeoData => {
  const canonicalPath = buildCategoryCanonicalPath(categoryRouteData)
  const categoryName = categoryRouteData.category.name || 'Categoria'
  const title = trimText(`${categoryName} | ${siteName}`, 70) || `${siteName}`
  const description =
    trimText(
      categoryRouteData.category.description || `Esplora i prodotti della categoria ${categoryName}.`,
      160,
    ) || DEFAULT_DESCRIPTION
  const image = pickCategoryImage(categoryRouteData)

  return {
    title,
    description,
    canonicalPath,
    alternates: buildDefaultSeoAlternates(canonicalPath),
    ...(image ? { image } : {}),
    ogType: 'website',
    pageType: 'category',
    entityName: categoryName,
    breadcrumbs: buildCategoryBreadcrumbs(categoryRouteData),
  }
}
